import React from 'react';
import { mergeClassNames, TrackingProvider } from '@givelify/utils';
import { Modal, styled } from '@mui/material';
import Div100vh from 'react-div-100vh';
import ModalHeader from './ModalHeader';
import { ModalSteps, ModalHorizontalSteps } from './ModalSteps';
import { ModalTopMessage } from './ModalTopMessage';
import { SubmitFooter } from './SubmitFooter';

type ModalContainerStyleProps = {
    hasFooter: boolean;
    steps: boolean;
    normalContentPadding?: {
        paddingLeft?: number;
        paddingRight?: number;
        paddingTop?: number;
        paddingBottom?: number;
    };
    smallContentPadding?: {
        paddingLeft?: number;
        paddingRight?: number;
        paddingTop?: number;
        paddingBottom?: number;
    };
};

const ModalContainer = styled(Div100vh, {
    shouldForwardProp: (prop) =>
        prop !== 'steps' &&
        prop !== 'hasFooter' &&
        prop !== 'normalContentPadding' &&
        prop !== 'smallContentPadding',
})<ModalContainerStyleProps>(
    ({ theme, steps, normalContentPadding, smallContentPadding }) => ({
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        transform: `translate(0, 0)`,
        width: '100vw',
        overflow: 'auto',
        background: theme.legacy.colors
            ? theme.legacy.colors.primaryWhite
            : '#ffffff',
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        '& .content': {
            boxSizing: 'border-box',
            paddingTop: normalContentPadding?.paddingTop ?? 40,
            paddingLeft: normalContentPadding?.paddingLeft ?? 16,
            paddingRight: normalContentPadding?.paddingRight ?? 16,
            flex: 1,
            [theme.breakpoints.down('sm')]: {
                paddingTop: smallContentPadding?.paddingTop ?? 24,
                paddingLeft: smallContentPadding?.paddingLeft ?? 16,
                paddingRight: smallContentPadding?.paddingRight ?? 16,
            },
            overflowY: 'auto',
        },
        '& .content-holder': {
            maxWidth: steps ? 1380 : 1259,
            minHeight: 117,
            margin: '0 auto',
            display: 'flex',
            width: '100%',
            boxSizing: 'border-box',
        },
        '& .content-flex': {
            overflow: 'visible',
            width: '100%',
            paddingBottom: normalContentPadding?.paddingBottom ?? 40,
            [theme.breakpoints.down('sm')]: {
                paddingBottom: smallContentPadding?.paddingBottom ?? 40,
            },
        },
        '& .footer': {
            zIndex: 1,
            backgroundColor: theme.legacy.colors?.primaryWhite,
            height: 90,
            boxShadow: '0px -1px 2px rgba(58, 76, 99, 0.2)',
            [theme.breakpoints.down('xs')]: {
                height: 72,
            },
        },
        [theme.breakpoints.down('sm')]: {
            '& .content-flex': {
                width: '100%',
            },
        },
    }),
);

export type FullWidthModalProps = {
    id?: string;
    testId?: string;
    name: string;
    heading: string;
    open: boolean;
    onClose: () => unknown;
    useBackArrow?: boolean;
    lockText?: string;
    retryMessage?: string | undefined;
    errorMessage?: string | undefined;
    steps?:
        | {
              totalSteps: number;
              currentStep: number;
          }
        | undefined;
    footer?:
        | {
              onCancel: () => void;
              onSubmit: () => void;
              cancelText: string;
              submitText: string;
              disableSubmit: boolean;
              isLoading: boolean;
          }
        | undefined;
    normalContentPadding?: {
        paddingLeft?: number;
        paddingRight?: number;
        paddingTop?: number;
        paddingBottom?: number;
    };
    smallContentPadding?: {
        paddingLeft?: number;
        paddingRight?: number;
        paddingTop?: number;
        paddingBottom?: number;
    };
    onStepClick?: (step: number) => void;
    className?: string;
    classContentName?: string;
    classFooterName?: string;
    trackPageVisit?: boolean;
};

export const FullWidthModal: React.FCC<FullWidthModalProps> = ({
    id,
    testId,
    name,
    heading,
    open,
    onClose,
    children,
    useBackArrow,
    retryMessage,
    errorMessage,
    lockText,
    footer,
    steps,
    normalContentPadding,
    smallContentPadding,
    onStepClick,
    className,
    classContentName,
    classFooterName,
    trackPageVisit = false,
}) => {
    if (!open) return null;
    return (
        <TrackingProvider
            pageName={`${name} Modal`}
            trackPageVisit={trackPageVisit}
        >
            <Modal
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                open
                aria-describedby="modal-title"
                aria-labelledby="modal-title"
                className={className}
                data-testid={testId}
                id={id}
                onClose={onClose}
            >
                <ModalContainer
                    hasFooter={!!footer}
                    normalContentPadding={normalContentPadding}
                    smallContentPadding={smallContentPadding}
                    steps={steps !== undefined}
                >
                    <ModalHeader
                        heading={heading}
                        lockText={lockText}
                        onClose={onClose}
                        useBackArrow={useBackArrow}
                    />
                    <div
                        className={mergeClassNames('content', classContentName)}
                    >
                        <div className="content-holder">
                            {steps && (
                                <ModalSteps
                                    currentStep={steps.currentStep}
                                    onStepClick={onStepClick}
                                    totalSteps={steps.totalSteps}
                                />
                            )}
                            <div className="content-flex">
                                {steps && (
                                    <ModalHorizontalSteps
                                        currentStep={steps.currentStep}
                                        onStepClick={onStepClick}
                                        totalSteps={steps.totalSteps}
                                    />
                                )}
                                <ModalTopMessage
                                    message={errorMessage}
                                    variant="error"
                                />
                                <ModalTopMessage
                                    message={retryMessage}
                                    variant="warning"
                                />
                                {children}
                            </div>
                        </div>
                    </div>
                    {footer && (
                        <div
                            className={mergeClassNames(
                                'footer',
                                classFooterName,
                            )}
                        >
                            <SubmitFooter
                                cancelText={footer.cancelText}
                                disableSubmit={footer.disableSubmit}
                                isLoading={footer.isLoading}
                                onCancel={footer.onCancel}
                                onSubmit={footer.onSubmit}
                                submitText={footer.submitText}
                                testId={testId}
                            />
                        </div>
                    )}
                </ModalContainer>
            </Modal>
        </TrackingProvider>
    );
};
